import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import { eventBus } from '../eventBus'

// Import side-effect: registers contracts with the responseContract registry.
import '../contracts/AiCredits_PublicPacksResponse'
import '../contracts/AiCredits_PurchaseResponse'
import '../contracts/AiCredits_PurchaseStatusResponse'
import '../contracts/AiCredits_PurchaseInput'

import type { AiCredits_PackRow } from '../contracts/AiCredits_PackRow'
import type { AiCredits_PurchaseResponseDados } from '../contracts/AiCredits_PurchaseResponse'
import type { AiCredits_PurchaseInput } from '../contracts/AiCredits_PurchaseInput'
import type { AiCredits_PublicPacksResponseDados } from '../contracts/AiCredits_PublicPacksResponse'

/**
 * useCreditPurchase — SOLE network owner for ai/credits/packs/available and
 * ai/credits/purchase routes (franqueado-facing).
 *
 * Separate from useCreditPacks (which is Full-staff-only CRUD). This composable
 * serves the F7 purchase flow.
 *
 * Module-scope singleton state. Call `_reset()` in afterEach for test isolation.
 */

export interface PendingPurchase {
  purchase_id: number
  status: string
  checkout_url: string
  charge_id: string
}

// ── Module-scope singleton state ────────────────────────────────────────────

const availablePacks = ref<AiCredits_PackRow[]>([])
const pendingPurchase = ref<PendingPurchase | null>(null)
/**
 * purchaseEnabled (OD-016) — mirrors the purchase_enabled field from
 * AiCredits_PublicPacksResponse. Set by listAvailablePacks(); null until
 * the first call completes. The CTA gates on this to avoid a second round-trip.
 */
const purchaseEnabled = ref<boolean | null>(null)
/**
 * emptyReason (D6) — mirrors dados.empty_reason from the last packs/available
 * response. 'pais_sem_mercado' means the franchise's country has no billing
 * market configured (named reason — the catalog is intentionally empty).
 * Null when packs exist or no named reason was given.
 */
const emptyReason = ref<string | null>(null)

// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Public composable ────────────────────────────────────────────────────────

export function useCreditPurchase() {
  /**
   * listAvailablePacks — GET ai/credits/packs/available.
   *
   * Returns only active packs (franqueado-facing; no inactive rows).
   * Populates the module-scope `availablePacks` ref.
   *
   * D6: `franquiaId` scopes the catalog to that franchise's market country —
   * without it the backend resolves the pinned BR default. Passing it is a
   * prerequisite for the pais_sem_mercado seam to work.
   */
  async function listAvailablePacks(franquiaId?: number): Promise<void> {
    const params = franquiaId != null ? `?franquia_id=${franquiaId}` : ''
    const raw = await httpService.get(
      sistemaUrl(`ai/credits/packs/available${params}`),
      { responseContract: 'AiCredits_PublicPacksResponse' },
    )
    const resp = raw as { dados: AiCredits_PublicPacksResponseDados }
    availablePacks.value = resp.dados?.rows ?? []
    purchaseEnabled.value = resp.dados?.purchase_enabled ?? false
    emptyReason.value = resp.dados?.empty_reason ?? null
    log('useCreditPurchase', 'listAvailablePacks', 'Packs disponíveis carregados', {
      count: availablePacks.value.length,
      purchase_enabled: purchaseEnabled.value,
      franquiaId,
      emptyReason: emptyReason.value,
    })
  }

  /**
   * initiatePurchase — POST ai/credits/purchase.
   *
   * Generates a deterministic idempotency key (UUID) for the request_id.
   * Sets `pendingPurchase` to the returned pending state (purchase_id, status,
   * checkout_url, charge_id). The wallet balance is NOT updated here — only
   * after pollPurchaseStatus reports `confirmed` does the wallet refetch fire.
   */
  async function initiatePurchase(franquia_id: number, pack_id: number, provider_code: string): Promise<PendingPurchase> {
    const body: AiCredits_PurchaseInput = {
      franquia_id,
      pack_id,
      provider_code,
      request_id: crypto.randomUUID(),
    }

    const raw = await httpService.post(
      sistemaUrl('ai/credits/purchase'),
      body,
      {
        inputContract: 'AiCredits_PurchaseInput',
        responseContract: 'AiCredits_PurchaseResponse',
        // 'pais_sem_mercado', 'pack_inativo', standing recusado… tudo isso
        // chega em HTTP 200 com success:false. Sem isto o modal segue para o
        // checkout com `checkout_url` undefined.
        throwOnEnvelopeFailure: 'purchase_failed',
      },
    )

    const resp = raw as { dados: AiCredits_PurchaseResponseDados }
    const purchase: PendingPurchase = {
      purchase_id: resp.dados.purchase_id,
      status: resp.dados.status,
      checkout_url: resp.dados.checkout_url,
      charge_id: resp.dados.charge_id,
    }

    pendingPurchase.value = purchase
    log('useCreditPurchase', 'initiatePurchase', 'Compra iniciada', {
      franquia_id,
      pack_id,
      purchase_id: purchase.purchase_id,
      status: purchase.status,
    })

    return purchase
  }

  /**
   * pollPurchaseStatus — bounded polling of GET ai/credits/purchase/{id}/status.
   *
   * Polls until status transitions to `confirmed` or `failed` (or the timeout
   * is reached). Injectable timing (intervalMs / timeoutMs) for fake-timer-
   * friendly testing.
   *
   * On `confirmed`:
   *   - updates `pendingPurchase.value.status`
   *   - emits `credit.topup.confirmed` on eventBus so the consumer (F7) can
   *     refetch the wallet. The composable itself does NOT mutate any balance.
   *
   * On `failed` or `expired` (C, 2026-08-06 — abandoned-purchase sweep):
   *   - updates `pendingPurchase.value.status`
   *   - throws an Error so the caller can surface the failure
   *   `expired` is a bookkeeping close, not proof the purchase can never
   *   succeed (a late webhook can still confirm it) — but from the polling
   *   franqueado's perspective there is nothing left to wait for, so it is
   *   treated the same as `failed` here.
   *
   * On timeout:
   *   - throws an Error; status remains `pending`
   */
  async function pollPurchaseStatus(
    purchase_id: number,
    options: { intervalMs?: number; timeoutMs?: number } = {},
  ): Promise<void> {
    const intervalMs = options.intervalMs ?? 3_000
    const timeoutMs = options.timeoutMs ?? 120_000

    const deadline = Date.now() + timeoutMs

    while (Date.now() < deadline) {
      const raw = await httpService.get(
        sistemaUrl(`ai/credits/purchase/${purchase_id}/status`),
        { responseContract: 'AiCredits_PurchaseStatusResponse' },
      )
      const resp = raw as { dados: { status: string; credits_granted?: number } }
      const status = resp.dados.status

      if (status === 'confirmed') {
        if (pendingPurchase.value) {
          pendingPurchase.value = { ...pendingPurchase.value, status: 'confirmed' }
        }
        log('useCreditPurchase', 'pollPurchaseStatus', 'Compra confirmada', { purchase_id })
        eventBus.emit('credit.topup.confirmed', { purchase_id })
        return
      }

      if (status === 'failed' || status === 'expired') {
        if (pendingPurchase.value) {
          pendingPurchase.value = { ...pendingPurchase.value, status }
        }
        log('useCreditPurchase', 'pollPurchaseStatus', 'Compra falhou ou expirou', { purchase_id, status })
        throw new Error(`Purchase ${purchase_id} ${status}`)
      }

      // Still pending — wait before the next poll.
      await new Promise<void>((resolve) => setTimeout(resolve, intervalMs))
    }

    throw new Error(`Purchase ${purchase_id} polling timed out after ${timeoutMs}ms`)
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    availablePacks.value = []
    pendingPurchase.value = null
    purchaseEnabled.value = null
    emptyReason.value = null
  }

  return {
    // reactive state
    availablePacks,
    pendingPurchase,
    purchaseEnabled,
    emptyReason,
    // methods
    listAvailablePacks,
    initiatePurchase,
    pollPurchaseStatus,
    _reset,
  }
}
